<template>
  <div id="command">
    <!-- Using key to rerender terminal component when EUI change -->
    <terminal :key="eui" :event-bus="eventBus" @onTerminalData="onTerminalData" @onTerminalReady="addTerminalLine" />

    <div class="alert alert-info mt-3">
      {{ $t('g-command:info') | capitalize }}
    </div>
  </div>
</template>

<script>
import { isEmpty } from 'lodash-es'
import { mapGetters } from 'vuex'
import EventBus from 'vue'
const Terminal = () => import(/* webpackChunkName: "l-xterm" */ './Terminal')

export default {
  titleKey: 'gatewayCommand',

  components: { Terminal },

  props: {
    eui: {
      type: String,
      default: ''
    }
  },

  data () {
    return {
      eventBus: null,
      currentCmd: ''
    }
  },

  computed: {
    ...mapGetters(['gateway']),

    disabled () {
      return isEmpty(this.gateway)
    }
  },

  watch: {
    gateway (oldGateway, newGateway) {
      // Only at init
      if (oldGateway.eui !== newGateway.eui) {
        this.addTerminalLine()
      }
    }
  },

  created () {
    // Using a empty Vue instance as event bus
    // We cannot use a store/props with a watch, it does not work when using the same key several times
    this.eventBus = new EventBus()
  },

  methods: {
    // Create new line on terminal
    addTerminalLine () {
      if (!isEmpty(this.gateway)) {
        this.eventBus.$emit('terminal-data', `[${this.gateway.eui}]# `)
      }
    },

    // When receiving data from browser to be sent to backend
    onTerminalData (data, evt = {}) {
      // Meta keys: alt, ctrl...
      const printable = (!evt.altKey && !evt.altGraphKey && !evt.ctrlKey && !evt.metaKey)
      // Blocked keys: tab (9), enter (13), arrows (37-40), del (46), start (36), end (35), page up/down (33-34), insert (45)...
      // => specific keyCode with charCode=0
      const blockedKey = ([9, 13, 33, 34, 35, 36, 37, 38, 39, 40, 45, 46].indexOf(evt.keyCode) !== -1 && evt.charCode === 0)

      if (evt.keyCode === 13 && this.currentCmd !== '') {
        // Hit enter
        this.eventBus.$emit('terminal-data', data + '\n')

        // Execute command
        this.executeCommand()
          .then(this.onCommandSuccess)
          .catch(this.onCommandError)
      } else if (evt.keyCode === 8) {
        // Hit backspace
        if (this.currentCmd.length > 0) {
          this.eventBus.$emit('terminal-data', '\b \b')
          this.currentCmd = this.currentCmd.slice(0, -1)
        }
      } else if (printable && !blockedKey) {
        this.eventBus.$emit('terminal-data', data)
        this.currentCmd += data
      }
    },

    async executeCommand (command) {
      return this.$api.createGatewayCommand(this.gateway, encodeURIComponent(this.currentCmd))
    },

    onCommandSuccess (response) {
      // Write result on success, replace \n by \r\n to have the proper newline format
      const content = response.data.content.replace(/\n/g, '\r\n')
      if (content) {
        this.eventBus.$emit('terminal-data', content)
      }

      // Reset command
      this.resetCommand()
    },

    onCommandError () {
      // Error message on error
      this.eventBus.$emit('terminal-data-newline', `[ERROR] Failed to execute command "${this.currentCmd}" on ${this.gateway.eui}`)

      // Reset command
      this.resetCommand()
    },

    resetCommand () {
      // Init new line
      this.addTerminalLine()
      // Reset current command
      this.currentCmd = ''
    }
  }
}
</script>
